/**
 * Default free baggage selection on the flight booking page.
 *
 * When the selected flight or booking validation carries
 * `includedefaultbaggage: true`, every passenger who can take baggage
 * (adults and children - infants never get a baggage SSR) is given the free
 * (0 amount) baggage of each sector by default. Passengers who already have
 * a baggage selected are left untouched.
 */
import { isEmpty } from '../../shared/helper-utils/appUtils';

/** Accepts both boolean true and the string "true". */
export const isDefaultBaggageEnabled = (source) =>
  String(source?.includedefaultbaggage ?? '')
    .trim()
    .toLowerCase() === 'true';

const toAmount = (value) => {
  if (isEmpty(value)) return null;
  const amount = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(amount) ? amount : null;
};

/** A baggage SSR is free only when the supplier sent an explicit 0. */
export const isFreeBaggage = (baggage) =>
  toAmount(baggage?.chargeableamount) === 0;

const isInfant = (row) => String(row?.paxtype ?? '').toLowerCase() === 'infant';

/**
 * @param {object} ssrRows          userSelectedSsrRows, keyed by sector
 * @param {object} baggageBySector  flightLegsWithBaggageRef.current
 * @returns {object} new rows when something was selected, else `ssrRows`
 *                   itself (callers compare by reference to know if it changed)
 */
export const applyDefaultFreeBaggage = (ssrRows, baggageBySector) => {
  if (!ssrRows || !baggageBySector) return ssrRows;
  let hasChanged = false;
  const nextRows = { ...ssrRows };

  Object.keys(baggageBySector).forEach((sector) => {
    const sectorRows = nextRows[sector];
    if (!sectorRows?.baggageRows) return;
    const freeBaggage = (baggageBySector[sector] || []).find(isFreeBaggage);
    if (!freeBaggage) return;

    const baggageRows = sectorRows.baggageRows.map((row) => {
      if (isInfant(row) || !isEmpty(row.ssrcode)) return row;
      hasChanged = true;
      return {
        ...row,
        ssrcode: freeBaggage.ssrcode,
        ssrname: freeBaggage.ssrname,
        ssrid: freeBaggage.ssrid,
        // Number, not the raw "0" - the fare summary adds these with `+=`.
        ssr_chargeableamount: 0,
      };
    });
    nextRows[sector] = { ...sectorRows, baggageRows };
  });

  return hasChanged ? nextRows : ssrRows;
};
